/**
 * Album Editor Component
 * Alpine.js component for managing album photos with grid layout, drag-and-drop reordering,
 * and all album editing capabilities (upload, delete, description, move, crop, reorder)
 */

import { uploadPhoto, deletePhoto, parseUploadError, createEmptySlot } from './photo-uploader.js';
import { editorBase } from './alpine-editor-base.js';

const ALBUM_ENDPOINT = '/v1/album';
const PHOTO_ENDPOINT = '/v1/photo';

/**
 * Album editor component factory
 * @param {Array} existingPhotos - Array of existing photos with properties
 * @param {string} albumId - The album ID
 * @param {string} albumCoverId - Current album cover photo ID
 * @param {Array} otherAlbums - List of other albums for move functionality
 * @param {boolean} isEditable - Whether album can be reordered/cover changed
 */
export function albumEditor(existingPhotos = [], albumId = null, albumCoverId = null, otherAlbums = [], isEditable = true) {
    const initialPhotos = existingPhotos.map(photo => ({
        id: String(photo.id),
        url: photo.url,
        thumbUrl: photo.thumbUrl || photo.url,
        fullUrl: photo.fullUrl,
        description: photo.description || '',
        width: photo.width,
        height: photo.height,
        thumbParams: photo.thumbParams || {},
        uploading: false,
        uploaded: true,
        progress: 100,
        error: null
    }));

    return {
        photos: initialPhotos,
        albumId: albumId,
        albumCoverId: String(albumCoverId || ''),
        otherAlbums: otherAlbums,
        isEditable: isEditable,

        // UI State
        error: null,
        globalDragOver: false,
        dragOverSlot: null,

        // Drag and drop reordering
        draggedIndex: null,
        dropTargetIndex: null,
        dropSide: null,
        isDragging: false,

        // Modals
        deleteConfirmIndex: null,
        moveModalIndex: null,
        descriptionModalIndex: null,
        cropModalIndex: null,

        // Upload
        uploadControllers: new Map(),

        // Edit state
        editingDescription: null,
        tempDescription: '',
        savingDescription: false,
        movingPhoto: false,
        settingCover: false,

        init() {
            document.addEventListener('thumbnail-updated', (e) => {
                const { photoId, thumbUrl } = e.detail;
                const photo = this.photos.find(p => p.id === photoId);
                if (photo) {
                    photo.thumbUrl = thumbUrl;
                }
            });
        },

        get uploadedCount() {
            return this.photos.filter(p => p.uploaded).length;
        },

        get isAnyUploading() {
            return this.photos.some(p => p.uploading);
        },

        get hasPhotos() {
            return this.photos.length > 0;
        },

        // ===== File Upload =====

        openFileDialog() {
            this.$refs.fileInput.click();
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files || []);
            if (!files.length) return;

            this.error = null;
            this.addFilesToQueue(files);
            event.target.value = '';
        },

        handleGlobalDrop(event) {
            this.globalDragOver = false;
            this.dragOverSlot = null;

            const files = Array.from(event.dataTransfer?.files || []);
            if (files.length) {
                this.addFilesToQueue(files);
            }
        },

        addFilesToQueue(files) {
            for (const file of files) {
                if (!file || !file.type.startsWith('image/')) continue;

                const blobUrl = URL.createObjectURL(file);
                const newPhoto = {
                    id: null,
                    url: blobUrl,
                    thumbUrl: blobUrl,
                    fullUrl: blobUrl,
                    description: '',
                    width: 0,
                    height: 0,
                    thumbParams: {},
                    uploading: true,
                    uploaded: false,
                    progress: 0,
                    error: null,
                    file: file
                };

                this.photos.push(newPhoto);
                this.doUpload(file, this.photos.length - 1);
            }
        },

        async doUpload(file, index) {
            const controller = new AbortController();
            this.uploadControllers.set(index, controller);

            try {
                const result = await uploadPhoto({
                    file,
                    albumId: this.albumId,
                    controller,
                    onProgress: (percent) => {
                        if (this.photos[index]) {
                            this.photos[index].progress = percent;
                        }
                    }
                });

                if (this.photos[index]) {
                    this.photos[index].uploading = false;
                    this.photos[index].uploaded = true;
                    this.photos[index].progress = 100;
                    this.photos[index].id = String(result.id);

                    // Update with server-provided URLs if available
                    if (result.thumb_url) {
                        this.photos[index].thumbUrl = result.thumb_url;
                    }
                    if (result.url) {
                        this.photos[index].url = result.url;
                    }

                    // Set as cover if this is the first photo
                    if (this.photos.length === 1 && !this.albumCoverId) {
                        this.albumCoverId = String(result.id);
                    }
                }
            } catch (err) {
                if (this.photos[index]) {
                    this.photos[index].uploading = false;
                    const errorMsg = parseUploadError(err);
                    if (errorMsg !== 'Upload cancelled') {
                        this.photos[index].error = errorMsg;
                    }
                }
            } finally {
                this.uploadControllers.delete(index);
            }
        },

        retryUpload(index) {
            const photo = this.photos[index];
            if (!photo || !photo.file) return;

            photo.error = null;
            photo.uploading = true;
            photo.progress = 0;
            this.doUpload(photo.file, index);
        },

        removeFailedUpload(index) {
            const photo = this.photos[index];
            if (photo && photo.url && photo.url.startsWith('blob:')) {
                URL.revokeObjectURL(photo.url);
            }
            this.photos.splice(index, 1);
        },

        // ===== Delete Photo =====

        confirmDeletePhoto(index) {
            if (this.photos[index].uploading) return;
            this.deleteConfirmIndex = index;
        },

        cancelDelete() {
            this.deleteConfirmIndex = null;
        },

        async executeDelete() {
            const index = this.deleteConfirmIndex;
            if (index === null) return;

            const photo = this.photos[index];
            this.deleteConfirmIndex = null;

            if (photo.id) {
                try {
                    await deletePhoto(photo.id);

                    // Update album cover if deleting cover photo
                    if (photo.id === this.albumCoverId && this.photos.length > 1) {
                        const nextPhoto = this.photos.find((p, i) => i !== index && p.id);
                        if (nextPhoto) {
                            this.albumCoverId = nextPhoto.id;
                        }
                    }

                    this.photos.splice(index, 1);
                } catch (err) {
                    this.showError(parseUploadError(err) || (window.t ? window.t('common.photo_delete_failed') : 'Failed to delete photo'));
                }
            } else {
                // Not uploaded yet, just remove locally
                if (photo.url && photo.url.startsWith('blob:')) {
                    URL.revokeObjectURL(photo.url);
                }
                this.photos.splice(index, 1);
            }
        },

        // ===== Set Album Cover =====

        async setAsCover(index) {
            const photo = this.photos[index];
            if (!photo || !photo.id || photo.uploading) return;
            if (photo.id === this.albumCoverId) return;

            const token = editorBase.getAuthToken();
            if (!token) {
                this.showError(window.t ? window.t('common.auth_required') : 'Authentication required');
                return;
            }

            this.settingCover = true;
            const oldCoverId = this.albumCoverId;

            try {
                const formData = new FormData();
                formData.append('album_cover_id', photo.id);

                const response = await fetch(`${ALBUM_ENDPOINT}/${this.albumId}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token
                    },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) {
                    const errorMessage = await editorBase.handleApiError(response, 'album_cover_id');
                    this.showError(errorMessage || (window.t ? window.t('common.set_cover_failed') : 'Failed to set album cover'));
                    return;
                }

                this.albumCoverId = photo.id;

                // Move to first position
                if (index > 0) {
                    const [movedPhoto] = this.photos.splice(index, 1);
                    this.photos.unshift(movedPhoto);
                    await this.savePhotoOrder();
                }
            } catch {
                this.showError(window.t ? window.t('common.set_cover_failed') : 'Failed to set album cover');
            } finally {
                this.settingCover = false;
            }
        },

        // ===== Description Editing =====

        openDescriptionModal(index) {
            this.descriptionModalIndex = index;
            this.tempDescription = this.photos[index].description || '';
        },

        cancelDescriptionEdit() {
            this.descriptionModalIndex = null;
            this.tempDescription = '';
        },

        async saveDescription() {
            const index = this.descriptionModalIndex;
            if (index === null) return;

            const photo = this.photos[index];
            if (!photo || !photo.id) return;

            const token = editorBase.getAuthToken();
            if (!token) {
                this.showError(window.t ? window.t('common.auth_required') : 'Authentication required');
                return;
            }

            this.savingDescription = true;

            try {
                const formData = new FormData();
                formData.append('description', this.tempDescription);

                const response = await fetch(`${PHOTO_ENDPOINT}/${photo.id}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token
                    },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) {
                    const errorMessage = await editorBase.handleApiError(response, 'description');
                    this.showError(errorMessage || (window.t ? window.t('common.save_description_failed') : 'Failed to save description'));
                    return;
                }

                this.photos[index].description = this.tempDescription;
                this.descriptionModalIndex = null;
                this.tempDescription = '';
            } catch {
                this.showError(window.t ? window.t('common.save_description_failed') : 'Failed to save description');
            } finally {
                this.savingDescription = false;
            }
        },

        // ===== Move to Album =====

        openMoveModal(index) {
            this.moveModalIndex = index;
        },

        cancelMove() {
            this.moveModalIndex = null;
        },

        async moveToAlbum(targetAlbumId) {
            const index = this.moveModalIndex;
            if (index === null || !targetAlbumId) return;

            const photo = this.photos[index];
            if (!photo || !photo.id) return;

            const token = editorBase.getAuthToken();
            if (!token) {
                this.showError(window.t ? window.t('common.auth_required') : 'Authentication required');
                return;
            }

            this.movingPhoto = true;

            try {
                const formData = new FormData();
                formData.append('album_id', targetAlbumId);

                const response = await fetch(`${PHOTO_ENDPOINT}/${photo.id}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token
                    },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) {
                    const errorMessage = await editorBase.handleApiError(response, 'album_id');
                    this.showError(errorMessage || (window.t ? window.t('common.move_photo_failed') : 'Failed to move photo'));
                    return;
                }

                // Remove from current album
                this.photos.splice(index, 1);
                this.moveModalIndex = null;

                // Update cover if we moved the cover photo
                if (photo.id === this.albumCoverId && this.photos.length > 0) {
                    this.albumCoverId = this.photos[0].id;
                }
            } catch {
                this.showError(window.t ? window.t('common.move_photo_failed') : 'Failed to move photo');
            } finally {
                this.movingPhoto = false;
            }
        },

        // ===== Crop Thumbnail =====

        openCropModal(index) {
            const photo = this.photos[index];
            if (!photo || !photo.id) return;

            // Remove Tailwind hidden class before jQuery's show()
            const cropBox = document.getElementById('cropBox_' + photo.id);
            if (cropBox) {
                cropBox.classList.remove('hidden');
            }

            // Use existing Jcrop implementation
            if (typeof window.showCrop === 'function') {
                window.showCrop(photo.id);
            }
        },

        // ===== Drag and Drop Reordering =====

        handleDragStart(event, index) {
            if (this.photos[index].uploading) {
                event.preventDefault();
                return;
            }

            this.draggedIndex = index;
            this.isDragging = true;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', index.toString());

            // Create a small drag preview image
            const photo = this.photos[index];
            const dragImage = document.createElement('div');
            dragImage.style.cssText = 'width: 100px; height: 100px; position: absolute; top: -1000px; left: -1000px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.3);';
            const img = document.createElement('img');
            img.src = photo.thumbUrl || photo.url;
            img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
            dragImage.appendChild(img);
            document.body.appendChild(dragImage);
            event.dataTransfer.setDragImage(dragImage, 50, 50);
            setTimeout(() => document.body.removeChild(dragImage), 0);
        },

        handleDragOver(event, index) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';

            // Detect if cursor is on left or right half of element
            const rect = event.currentTarget.getBoundingClientRect();
            const midpoint = rect.left + rect.width / 2;
            const isRightHalf = event.clientX > midpoint;

            // If on right half, target the next position
            this.dropTargetIndex = isRightHalf ? index + 1 : index;
            this.dropSide = isRightHalf ? 'right' : 'left';
        },

        handleDragLeave(event, index) {
            // Only clear if we're actually leaving this element
            const rect = event.currentTarget.getBoundingClientRect();
            const x = event.clientX;
            const y = event.clientY;
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                this.dropTargetIndex = null;
                this.dropSide = null;
            }
        },

        handleDragEnd() {
            this.draggedIndex = null;
            this.dropTargetIndex = null;
            this.dropSide = null;
            this.isDragging = false;
        },

        async handleDrop(event, targetIndex) {
            event.preventDefault();

            const sourceIndex = this.draggedIndex;
            // Use the computed drop target which accounts for left/right side
            let insertIndex = this.dropTargetIndex !== null ? this.dropTargetIndex : targetIndex;

            this.draggedIndex = null;
            this.dropTargetIndex = null;
            this.dropSide = null;
            this.isDragging = false;

            if (sourceIndex === null) return;
            if (this.photos[sourceIndex].uploading) return;

            // Adjust insert index if moving forward (since we'll remove source first)
            if (sourceIndex < insertIndex) {
                insertIndex--;
            }

            // Don't do anything if dropping in same position
            if (sourceIndex === insertIndex) return;

            // Reorder in array
            const [movedPhoto] = this.photos.splice(sourceIndex, 1);
            this.photos.splice(insertIndex, 0, movedPhoto);

            // Update album cover if first photo changed
            if (this.photos[0] && this.photos[0].id) {
                const newFirstId = this.photos[0].id;
                if (newFirstId !== this.albumCoverId) {
                    this.albumCoverId = newFirstId;
                }
            }

            // Save new order to server
            await this.savePhotoOrder();
        },

        async savePhotoOrder() {
            const token = editorBase.getAuthToken();
            if (!token) {
                this.showError(window.t ? window.t('common.auth_required') : 'Authentication required');
                return;
            }

            const photoIds = this.photos
                .filter(p => p.id)
                .map(p => p.id)
                .join(',');

            if (!photoIds) return;

            try {
                const formData = new FormData();
                formData.append('photo_list', photoIds);
                formData.append('album_cover_id', this.albumCoverId);

                const response = await fetch(`${ALBUM_ENDPOINT}/${this.albumId}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token
                    },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) {
                    const errorMessage = await editorBase.handleApiError(response, 'photo_list');
                    this.showError(errorMessage || (window.t ? window.t('common.save_order_failed') : 'Failed to save photo order'));
                }
            } catch (err) {
                this.showError(window.t ? window.t('common.save_order_failed') : 'Failed to save photo order');
            }
        },

        // ===== View Photo =====

        openPhoto(index) {
            const photo = this.photos[index];
            if (!photo || !photo.id || photo.uploading || photo.error) return;

            const url = `/app/photo?pid=${photo.id}&aid=${this.albumId}`;
            window.location.href = url;
        },

        // ===== Utility Methods =====

        showError(message) {
            this.error = message;
        },

        hideError() {
            this.error = null;
        },

        isCover(index) {
            const photo = this.photos[index];
            return photo && photo.id && photo.id === this.albumCoverId;
        },

        getPhotoClasses(index) {
            const classes = [];
            if (this.draggedIndex === index) {
                classes.push('opacity-50', 'scale-95');
            }
            if (this.dropTargetIndex === index && this.draggedIndex !== index) {
                classes.push('ring-2', 'ring-blue-500', 'ring-offset-2');
            }
            return classes.join(' ');
        }
    };
}
